import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { getFlowOSServices, getPlans, workspacesApi } from "../../actions";
import { brand } from "../../theme/businessFlowsTheme";
import { canPlatform } from "../../utils/permissions";
import DateField from "../app/DateField";
import { Hint, Refusal, Surface, menuPaper } from "./WorkspaceUi";
import { STATUSES, STATUS_LABELS, formatDate, planLabel, seatText } from "./workspaceFormat";

// Names for the services this release knows, until the list has loaded.
const SERVICE_TITLES = { trends: "Market Trends", debate: "Process Debate", bpmbrain: "Process Analysis" };

// A stored whole number can arrive as a Neo4j Integer ({ low, high }).
const num = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return typeof value.low === "number" ? value.low : null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const day = (value) => (value ? String(value).slice(0, 10) : "");

function Fact({ label, children }) {
  return (
    <Box sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { sm: 1 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography component="div" sx={{ fontWeight: 600, mt: 0.25 }}>
        {children || "—"}
      </Typography>
    </Box>
  );
}

// Plan, seats, expiry and FlowOS. bpExperts changes them; a workspace's own
// admins see them read-only.
export default function PlanTab({ workspace, user, notify, onChanged }) {
  const mayBill = canPlatform(user, "subscriptions.manage");
  const subscription = workspace.subscription || {};
  const [plans, setPlans] = useState(null);
  const [services, setServices] = useState(null);
  const [form, setForm] = useState({
    plan: subscription.plan || "trial",
    status: subscription.status || "active",
    // What this workspace stores first: `seats.allowed` is null while plans
    // decide nothing, so it can no longer be the source of the number here.
    seats: String(num(subscription.seats) ?? num(workspace.seats?.allowed) ?? ""),
    currentPeriodEnd: day(subscription.currentPeriodEnd),
  });
  // services null: follow the plan; an array: this workspace's own grant.
  const [flowos, setFlowos] = useState({
    services: Array.isArray(subscription.flowosServices) ? subscription.flowosServices : null,
    monthlyCredits: num(subscription.flowosMonthlyCredits) ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Whether a plan is allowed to refuse anything at all (off since 28 Sep
  // 2026 - backend/services/planEnforcement.js). Until the answer is in,
  // assume it does, so the notice doesn't flash on every open.
  const [enforced, setEnforced] = useState(true);

  useEffect(() => {
    let live = true;
    getPlans().then((res) => {
      if (!live) return;
      setPlans(res?.success ? res.content?.plans || [] : []);
      if (res?.success) setEnforced(Boolean(res.content?.enforced));
      // A workspace that never stored a seat count still needs a number in
      // the box, since the save asks for one: the plan's own default.
      const planned = num((res?.content?.plans || []).find((p) => p.key === (subscription.plan || "trial"))?.entitlements?.seats);
      if (planned) setForm((f) => (f.seats === "" ? { ...f, seats: String(planned) } : f));
    });
    getFlowOSServices().then((res) => live && setServices(res?.success ? res.content?.services || [] : []));
    return () => {
      live = false;
    };
    // The plan's own seat default is read here, so this re-reads when the
    // workspace's plan changes. It only fills an empty seats box, so a re-run
    // never overwrites a number an admin has typed.
  }, [subscription.plan]);

  const titleOf = (key) => (services || []).find((s) => s.service === key)?.title || SERVICE_TITLES[key] || key;
  const servicesOfPlan = (planKey) => (plans || []).find((p) => p.key === planKey)?.entitlements?.["flowos.services"] || [];
  const listOf = (keys) => keys.map(titleOf).join(", ");

  // Said in both views, because both look like they are setting access.
  const plansDecideNothing = !enforced && (
    <Hint next="Who sees what is set under Views: a view belongs to one database, and giving someone a view gives them that database.">
      <strong>This plan doesn't decide access.</strong> It records what this workspace is billed for. What its people may
      open, and what they see in it, is the view their group gives them on a database; what they may change is that group's
      edit mode and their workspace role. Seats, the end date and the FlowOS grant below are not refusing anything today.
    </Hint>
  );

  if (!mayBill) {
    const own = Array.isArray(subscription.flowosServices) ? subscription.flowosServices : null;
    const fromPlan = servicesOfPlan(workspace.plan);
    return (
      <Stack spacing={2}>
        {plansDecideNothing}
        <Surface sx={{ p: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} flexWrap="wrap" useFlexGap>
            <Fact label="Plan">{planLabel(workspace.plan)}</Fact>
            <Fact label="Status">{STATUS_LABELS[subscription.status] || subscription.status}</Fact>
            <Fact label="Seats">{seatText(workspace.seats)}</Fact>
            <Fact label="Renews or ends on">{formatDate(subscription.currentPeriodEnd || subscription.trialEndsAt)}</Fact>
            <Fact label="FlowOS">
              {own ? (
                own.length ? (
                  listOf(own)
                ) : (
                  "Off for this workspace"
                )
              ) : plans === null ? (
                <Skeleton width={140} />
              ) : fromPlan.length ? (
                listOf(fromPlan)
              ) : (
                "Not included in the plan"
              )}
            </Fact>
          </Stack>
        </Surface>
        <Typography variant="body2" color="text.secondary">
          To change the plan or add seats, contact bpExperts.
        </Typography>
      </Stack>
    );
  }

  const planServices = servicesOfPlan(form.plan);
  const granted = Array.isArray(flowos.services);
  const seatsNumber = Number(form.seats);
  const seatsValid = Number.isInteger(seatsNumber) && seatsNumber >= 1;
  const creditsValid = flowos.monthlyCredits === "" || (Number.isInteger(Number(flowos.monthlyCredits)) && Number(flowos.monthlyCredits) >= 0);
  const planOptions = plans?.length ? plans.map((p) => ({ key: p.key, label: p.name || planLabel(p.key) })) : [{ key: form.plan, label: planLabel(form.plan) }];

  const save = async () => {
    setBusy(true);
    setError("");
    const res = await workspacesApi.savePlan(workspace.id, {
      plan: form.plan,
      status: form.status,
      seats: seatsNumber,
      currentPeriodEnd: form.currentPeriodEnd || null,
      flowos: {
        services: flowos.services,
        monthlyCredits: flowos.monthlyCredits === "" ? null : Number(flowos.monthlyCredits),
      },
    });
    setBusy(false);
    if (!res.success) {
      setError(res.msg || "Couldn't save the plan.");
      return;
    }
    notify(res.msg || "Plan saved.");
    onChanged();
  };

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
      {plansDecideNothing}
      <Surface sx={{ p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, mb: 2 }}>Subscription</Typography>
        <Stack spacing={2}>
          <TextField
            select
            label="Plan"
            value={form.plan}
            SelectProps={{ MenuProps: menuPaper }}
            onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
          >
            {planOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={form.status}
              SelectProps={{ MenuProps: menuPaper }}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Seats"
              required
              value={form.seats}
              onChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))}
              inputProps={{ min: 1, step: 1 }}
              error={!seatsValid}
              helperText={seatsValid ? `In use: ${seatText(workspace.seats)}` : "Enter a whole number of seats, at least 1."}
            />
          </Stack>
          <DateField
            label="Renews or ends on"
            value={form.currentPeriodEnd}
            onChange={(value) => setForm((f) => ({ ...f, currentPeriodEnd: value }))}
            helperText="The day the plan renews or ends."
          />
        </Stack>
      </Surface>

      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>FlowOS</Typography>
            <Typography variant="body2" color="text.secondary">
              {granted
                ? flowos.services.length
                  ? `Chosen for this workspace: ${listOf(flowos.services)}`
                  : "FlowOS is off for this workspace"
                : planServices.length
                ? `Follows the plan: ${listOf(planServices)}`
                : "Follows the plan, which includes no FlowOS services"}
            </Typography>
          </Box>
          <Switch
            checked={granted}
            onChange={(e) =>
              setFlowos(e.target.checked ? { services: [...planServices], monthlyCredits: "" } : { services: null, monthlyCredits: "" })
            }
            inputProps={{ "aria-label": "Choose FlowOS services for this workspace" }}
          />
        </Stack>
        {granted && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {services === null ? (
              <Skeleton variant="rounded" height={32} width={280} />
            ) : (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap role="group" aria-label="FlowOS services">
                {services.map((option) => {
                  const on = flowos.services.includes(option.service);
                  return (
                    <Chip
                      key={option.service}
                      label={option.title || titleOf(option.service)}
                      aria-pressed={on}
                      onClick={() =>
                        setFlowos((current) => ({
                          ...current,
                          services: on ? current.services.filter((s) => s !== option.service) : [...current.services, option.service],
                        }))
                      }
                      sx={{
                        fontWeight: 600,
                        bgcolor: on ? brand.ink : "transparent",
                        color: on ? "#fff" : "text.primary",
                        border: `1px solid ${on ? brand.ink : brand.line}`,
                        "&:hover": { bgcolor: on ? brand.inkSoft : brand.canvas },
                        "&.Mui-focusVisible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2, bgcolor: on ? brand.inkSoft : brand.canvas },
                      }}
                    />
                  );
                })}
              </Stack>
            )}
            <TextField
              type="number"
              label="Credits per month"
              value={flowos.monthlyCredits}
              onChange={(e) => setFlowos((current) => ({ ...current, monthlyCredits: e.target.value }))}
              error={!creditsValid}
              helperText={creditsValid ? "Leave empty to use the plan's budget." : "Enter a whole number of 0 or more."}
              inputProps={{ min: 0, step: 1 }}
            />
          </Stack>
        )}
      </Surface>

      {error && <Refusal onClose={() => setError("")}>{error}</Refusal>}
      <Box>
        <LoadingButton variant="contained" loading={busy} disabled={!seatsValid || !creditsValid} onClick={save}>
          Save plan
        </LoadingButton>
      </Box>
    </Stack>
  );
}
